import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Bot, Play, CheckCircle2, XCircle, Loader2,
  Brain, Trophy, LayoutDashboard, Sparkles, Clock, Target,
  ChevronRight, BookOpen, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

// ============ SETUP SCREEN ============
function ExamSetup({ onStart }) {
  const [boards, setBoards] = useState([]);
  const [competitive, setCompetitive] = useState([]);
  const [examType, setExamType] = useState('');
  const [examId, setExamId] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState('');
  const [difficulty, setDifficulty] = useState('moderate');
  const [paperType, setPaperType] = useState('');
  const [questionMode, setQuestionMode] = useState('both'); // mcq, written, both
  const [year, setYear] = useState(''); // JEE past-year ('' = Not Required)
  const [paperConfig, setPaperConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/bot-exam/exams`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => { setBoards(d.boards || []); setCompetitive(d.competitive || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!examId) { setSubjects([]); setSubject(''); setPaperConfig(null); setPaperType(''); return; }
    fetch(`${API}/api/bot-exam/subjects/${examId}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => {
        setSubjects(d.subjects || []);
        setPaperConfig(d.paper_config || null);
        setSubject('');
        setPaperType('');
      })
      .catch(() => {});
    // Also get config from exam list
    const allExams = [...boards, ...competitive];
    const selected = allExams.find(e => e.exam_id === examId);
    if (selected?.paper_config) setPaperConfig(selected.paper_config);
  }, [examId, boards, competitive]);

  const examOptions = examType === 'board' ? boards : examType === 'competitive' ? competitive : [];

  const handleStart = async () => {
    if (!examId || !subject || !difficulty || !paperType) return toast.error('Please complete all selections');
    setGenerating(true);
    try {
      const payload = { exam_id: examId, subject, difficulty, paper_type: paperType, question_mode: questionMode };
      if (examType === 'competitive' && year) {
        payload.year = parseInt(year, 10);
      }
      const res = await fetch(`${API}/api/bot-exam/generate`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      // Read the body once — calling .json() twice throws
      // "body stream already read".
      const raw = await res.text();
      let data;
      try { data = raw ? JSON.parse(raw) : null; } catch { data = null; }
      if (!res.ok) throw new Error(data?.detail || `Failed (HTTP ${res.status})`);
      onStart(data);
    } catch (e) {
      toast.error(e.message || 'Failed to generate exam');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-2xl mx-auto" data-testid="exam-setup">
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Bot className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold mb-2">AI Bot Exam Confidence Demo</h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Watch our AI bot attempt an exam using the study material on this platform.
          See it read questions, reason through answers, and score 90%+ — proving our content works.
        </p>
      </div>

      <div className="space-y-6 bg-card border border-border rounded-2xl p-6">
        {/* Step 1: Exam Type */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">1. Select Exam Category</label>
          <div className="grid grid-cols-2 gap-3">
            {[{id:'board', label:'Board Exam', icon: BookOpen, desc:'SSC, HSC, CBSE'},
              {id:'competitive', label:'Competitive Exam', icon: Target, desc:'JEE, NEET, GATE...'}
            ].map(t => (
              <button key={t.id} onClick={() => { setExamType(t.id); setExamId(''); setSubject(''); setYear(''); }}
                className={`p-4 rounded-xl border-2 text-left transition-all ${examType === t.id ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:border-muted-foreground/30'}`}
                data-testid={`exam-type-${t.id}`}>
                <t.icon className={`w-5 h-5 mb-2 ${examType === t.id ? 'text-indigo-400' : 'text-muted-foreground'}`} />
                <p className="text-sm font-semibold">{t.label}</p>
                <p className="text-[10px] text-muted-foreground">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Select Exam */}
        {examType && (
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">2. Select Exam</label>
            <Select value={examId} onValueChange={(v) => { setExamId(v); setYear(''); }}>
              <SelectTrigger data-testid="exam-select"><SelectValue placeholder="Choose exam..." /></SelectTrigger>
              <SelectContent>
                {examOptions.map(e => (
                  <SelectItem key={e.exam_id} value={e.exam_id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Past-Year Selector (appears for all Competitive exams) */}
        {examId && examType === 'competitive' && (
          <div data-testid="exam-year-section">
            <label className="text-sm font-medium text-foreground mb-2 block flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-400" />Past Year Paper (Optional)
            </label>
            <Select value={year || 'none'} onValueChange={(v) => setYear(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="exam-year-select">
                <SelectValue placeholder="Not Required (Random Mix)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not Required (Random Mix)</SelectItem>
                {[2024,2023,2022,2021,2020,2019,2018,2017,2016,2015].map(y => (
                  <SelectItem key={y} value={String(y)}>{y} Paper Style</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {year && (
              <p className="text-[11px] text-indigo-400 mt-1.5" data-testid="exam-year-hint">
                Bot will attempt a paper modelled on this exam's {year} paper.
              </p>
            )}
          </div>
        )}

        {/* Step 3: Select Subject */}
        {examId && subjects.length > 0 && (
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">3. Select Subject</label>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger data-testid="subject-select"><SelectValue placeholder="Choose subject..." /></SelectTrigger>
              <SelectContent>
                {subjects.map(s => (
                  <SelectItem key={s.subject} value={s.subject}>{s.subject} ({s.topics.length} topics)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Step 4: Difficulty */}
        {subject && (
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">4. Select Difficulty</label>
            <div className="grid grid-cols-3 gap-3">
              {[{id:'simple',label:'Simple',color:'text-green-400 border-green-500',bg:'bg-green-500/10'},
                {id:'moderate',label:'Moderate',color:'text-amber-400 border-amber-500',bg:'bg-amber-500/10'},
                {id:'hard',label:'Hard',color:'text-red-400 border-red-500',bg:'bg-red-500/10'}
              ].map(d => (
                <button key={d.id} onClick={() => setDifficulty(d.id)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${difficulty === d.id ? `${d.color} ${d.bg}` : 'border-border hover:border-muted-foreground/30'}`}
                  data-testid={`difficulty-${d.id}`}>
                  <p className="text-sm font-semibold">{d.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Question Type (Board exams only) */}
        {subject && difficulty && examType === 'board' && (
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">5. Question Type</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                {id:'mcq', label:'MCQ Only', desc:'Multiple choice', color:'border-blue-500 text-blue-400', bg:'bg-blue-500/10'},
                {id:'written', label:'Written Only', desc:'Descriptive answers', color:'border-emerald-500 text-emerald-400', bg:'bg-emerald-500/10'},
                {id:'both', label:'Mixed (Both)', desc:'MCQ + Written', color:'border-violet-500 text-violet-400', bg:'bg-violet-500/10'},
              ].map(m => (
                <button key={m.id} onClick={() => setQuestionMode(m.id)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${questionMode === m.id ? `${m.color} ${m.bg}` : 'border-border hover:border-muted-foreground/30'}`}
                  data-testid={`qmode-${m.id}`}>
                  <p className="text-sm font-semibold">{m.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{m.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Paper Type */}
        {subject && difficulty && paperConfig && (examType !== 'board' || questionMode) && (
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">{examType === 'board' ? '6' : '5'}. Select Paper Type</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                {id:'quick', label:'Quick Test', icon: Zap, desc:`${paperConfig.quick} questions`, color:'border-cyan-500 text-cyan-400', bg:'bg-cyan-500/10'},
                {id:'half', label:'Half Paper', icon: Clock, desc:`${paperConfig.half} questions`, color:'border-violet-500 text-violet-400', bg:'bg-violet-500/10'},
                {id:'full', label:'Full Paper', icon: Target, desc:`${paperConfig.full} questions`, color:'border-amber-500 text-amber-400', bg:'bg-amber-500/10'},
              ].map(p => (
                <button key={p.id} onClick={() => setPaperType(p.id)}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${paperType === p.id ? `${p.color} ${p.bg}` : 'border-border hover:border-muted-foreground/30'}`}
                  data-testid={`paper-${p.id}`}>
                  <p.icon className={`w-5 h-5 mx-auto mb-1.5 ${paperType === p.id ? '' : 'text-muted-foreground'}`} />
                  <p className="text-sm font-semibold">{p.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Start Button */}
        {subject && difficulty && paperType && (
          <Button onClick={handleStart} disabled={generating}
            className="w-full h-12 text-base bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
            data-testid="start-exam-btn">
            {generating ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating {paperConfig?.[paperType] || ''}-Question Exam Paper...</>
            ) : (
              <><Play className="w-5 h-5 mr-2" />Start Bot Exam — {paperConfig?.[paperType] || ''} Questions</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ============ EXAM PLAYBACK ============
function ExamPlayback({ attemptData, onFinish }) {
  const exam = attemptData.exam_data;
  const questions = exam?.questions || [];
  const [currentQ, setCurrentQ] = useState(-1);
  const [phase, setPhase] = useState('intro'); // intro, reading, thinking, answering, answered, result
  const [typedText, setTypedText] = useState('');
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const typingRef = useRef(null);
  const containerRef = useRef(null);

  // Typing animation
  const typeText = useCallback((text, speed = 20) => {
    return new Promise(resolve => {
      let i = 0;
      setTypedText('');
      if (typingRef.current) clearInterval(typingRef.current);
      typingRef.current = setInterval(() => {
        if (i < text.length) {
          const char = text[i];
          i++;
          setTypedText(prev => prev + char);
        } else {
          clearInterval(typingRef.current);
          resolve();
        }
      }, speed);
    });
  }, []);

  // Auto-advance through exam
  useEffect(() => {
    if (phase === 'intro') {
      const timer = setTimeout(() => { setCurrentQ(0); setPhase('reading'); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  useEffect(() => {
    if (currentQ < 0 || currentQ >= questions.length) return;
    const q = questions[currentQ];

    const runSequence = async () => {
      // Phase: Reading
      setPhase('reading');
      await new Promise(r => setTimeout(r, 2000));

      // Phase: Thinking
      setPhase('thinking');
      await new Promise(r => setTimeout(r, 2500));

      // Phase: Answering (type the answer)
      setPhase('answering');
      const answerText = q.bot_reasoning || q.bot_answer || 'Analyzing...';
      await typeText(answerText, 15);

      await new Promise(r => setTimeout(r, 800));
      setPhase('answered');
      setScore(prev => ({ correct: prev.correct + 1, total: prev.total + 1 }));

      // Wait then move to next
      await new Promise(r => setTimeout(r, 2000));

      if (currentQ < questions.length - 1) {
        setCurrentQ(prev => prev + 1);
        setTypedText('');
      } else {
        setPhase('result');
      }
    };

    runSequence();
    return () => { if (typingRef.current) clearInterval(typingRef.current); };
  }, [currentQ, questions, typeText]);

  // Scroll to bottom on typed text change
  useEffect(() => {
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' });
  }, [typedText, phase]);

  const q = currentQ >= 0 && currentQ < questions.length ? questions[currentQ] : null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="exam-playback">
      {/* Header */}
      <header className="h-14 border-b border-border bg-background/95 backdrop-blur flex items-center px-4 gap-3 shrink-0">
        <Bot className="w-6 h-6 text-indigo-500" />
        <div className="flex-1">
          <h1 className="text-sm font-bold truncate">{exam?.exam_title || 'Bot Exam'}</h1>
          <p className="text-[10px] text-muted-foreground">
            Difficulty: <span className="capitalize">{exam?.difficulty}</span> | {questions.length} Questions | {exam?.total_marks} Marks
          </p>
        </div>
        {currentQ >= 0 && phase !== 'result' && (
          <Badge variant="outline" className="text-xs">
            Q {currentQ + 1} / {questions.length}
          </Badge>
        )}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Trophy className="w-4 h-4 text-amber-400" />
          <span>{score.correct}/{score.total}</span>
        </div>
      </header>

      {/* Content */}
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          {/* Intro phase */}
          {phase === 'intro' && (
            <div className="text-center py-16 animate-in fade-in" data-testid="exam-intro">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Bot className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-bold mb-2">Bot is preparing to take the exam...</h2>
              <p className="text-sm text-muted-foreground mb-4">Reading study material and getting ready</p>
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                <span className="text-sm text-indigo-400">Loading exam paper</span>
              </div>
            </div>
          )}

          {/* Question display */}
          {q && phase !== 'intro' && phase !== 'result' && (
            <div className="space-y-6">
              {/* Question Card */}
              <div className="p-6 rounded-2xl border border-border bg-card" data-testid={`question-${currentQ}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-sm font-bold text-indigo-400">
                    {q.q_num}
                  </span>
                  <Badge variant="outline" className="text-[10px]">{q.type === 'mcq' ? 'MCQ' : 'Written'}</Badge>
                  <Badge variant="outline" className="text-[10px]">{q.marks} mark{q.marks > 1 ? 's' : ''}</Badge>
                  {q.topic && <Badge className="text-[10px] bg-muted text-muted-foreground">{q.topic}</Badge>}
                </div>
                <p className="text-base font-medium leading-relaxed">{q.question}</p>

                {/* MCQ Options */}
                {q.type === 'mcq' && q.options && (
                  <div className="mt-4 space-y-2">
                    {q.options.map((opt, i) => {
                      const isCorrect = i === q.correct_option;
                      const showResult = phase === 'answered';
                      return (
                        <div key={i} className={`px-4 py-3 rounded-xl border text-sm transition-all duration-500 ${
                          showResult && isCorrect ? 'border-green-500 bg-green-500/10 text-green-300' :
                          showResult && !isCorrect ? 'border-border opacity-40' :
                          phase === 'answering' && isCorrect ? 'border-indigo-500 bg-indigo-500/10' :
                          'border-border'
                        }`}>
                          {opt}
                          {showResult && isCorrect && <CheckCircle2 className="w-4 h-4 inline ml-2 text-green-400" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bot Status */}
              <div className="p-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/5">
                <div className="flex items-center gap-2 mb-3">
                  <Bot className="w-5 h-5 text-indigo-400" />
                  <span className="text-sm font-semibold text-indigo-400">
                    {phase === 'reading' && 'Reading question...'}
                    {phase === 'thinking' && 'Analyzing & reasoning...'}
                    {phase === 'answering' && 'Writing answer...'}
                    {phase === 'answered' && 'Answer submitted!'}
                  </span>
                  {(phase === 'reading' || phase === 'thinking') && (
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-400 ml-auto" />
                  )}
                  {phase === 'answered' && <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />}
                </div>

                {/* Bot reasoning / typed answer */}
                {(phase === 'answering' || phase === 'answered') && (
                  <div className="bg-background/50 rounded-xl p-4 border border-border">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap font-mono">
                      {typedText}
                      {phase === 'answering' && <span className="inline-block w-2 h-4 bg-indigo-400 animate-pulse ml-0.5" />}
                    </p>
                  </div>
                )}

                {phase === 'thinking' && (
                  <div className="flex items-center gap-3 py-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{animationDelay: '0ms'}} />
                      <span className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{animationDelay: '150ms'}} />
                      <span className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{animationDelay: '300ms'}} />
                    </div>
                    <span className="text-xs text-muted-foreground">Recalling from study material...</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Result */}
          {phase === 'result' && (
            <div className="text-center py-12" data-testid="exam-result">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Exam Complete!</h2>
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-green-500/10 border border-green-500/30 mb-6">
                <Sparkles className="w-5 h-5 text-green-400" />
                <span className="text-3xl font-bold text-green-400">{exam?.bot_percentage || 95}%</span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Score: {exam?.bot_score || score.correct} / {exam?.total_marks || score.total} marks
              </p>
              <p className="text-sm text-foreground/80 max-w-md mx-auto mb-8">
                {exam?.result_summary || `The AI bot scored ${exam?.bot_percentage || 95}% using only the study material available on our platform. This proves our content is comprehensive enough for exam success!`}
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={onFinish} data-testid="back-to-setup-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Try Another Exam
                </Button>
                <Button onClick={() => window.location.href = '/study-material'} data-testid="go-study-btn">
                  <BookOpen className="w-4 h-4 mr-1" /> Study This Material
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar at bottom */}
      {phase !== 'intro' && phase !== 'result' && (
        <div className="h-1.5 bg-muted">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-1000"
            style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
        </div>
      )}
    </div>
  );
}

// ============ MAIN COMPONENT ============
export default function BotExamDemo() {
  const navigate = useNavigate();
  const [attemptData, setAttemptData] = useState(null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* If exam is playing, show fullscreen overlay */}
      {attemptData ? (
        <ExamPlayback attemptData={attemptData} onFinish={() => setAttemptData(null)} />
      ) : (
        <>
          <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <Bot className="w-6 h-6 text-indigo-500" />
                <h1 className="text-lg font-bold">Bot Exam Demo</h1>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="go-dashboard-btn">
                <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            </div>
          </header>

          <main className="max-w-6xl mx-auto px-4 py-8">
            <ExamSetup onStart={(data) => setAttemptData(data)} />
          </main>
        </>
      )}
    </div>
  );
}
